const MANUAL_PAYMENT_TYPES = new Set([
  "upi",
  "imps",
  "manual",
  "manual_upi",
  "bank_transfer",
]);

const ACTIVE_STATUSES = new Set(["active", "enabled", "true", "1"]);

function formatAmount(value) {
  if (value == null || value === "") return "0";
  const num = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-US");
}

function isActiveStatus(status) {
  if (typeof status === "boolean") return status;
  return ACTIVE_STATUSES.has(String(status || "").toLowerCase());
}

function isManualPaymentMethod(method) {
  const type = String(method.type || "").toLowerCase();
  const name = String(method.name || "").toLowerCase();

  if (MANUAL_PAYMENT_TYPES.has(type)) return true;
  if (["upi", "imps"].includes(name)) return true;

  const metadata = method.metadata || {};
  return Boolean(
    metadata.upiId ||
      metadata.upi_id ||
      metadata.accountNo ||
      metadata.account_no
  );
}

function mapPaymentDetails(metadata = {}) {
  const upiDetails = metadata.upi_details || metadata.upiDetails || {};
  const bankDetails = metadata.bank_details || metadata.bankDetails || {};

  return {
    upiId:
      metadata.upiId ||
      metadata.upi_id ||
      upiDetails.upiId ||
      upiDetails.upi_id ||
      "",
    qrCodeUrl:
      metadata.qrCodeUrl ||
      metadata.qr_code_url ||
      upiDetails.qrCodeUrl ||
      upiDetails.qr_code_url ||
      "",
    accountNo:
      metadata.accountNo ||
      metadata.account_no ||
      bankDetails.accountNo ||
      bankDetails.account_number ||
      "",
    ifscCode:
      metadata.ifscCode ||
      metadata.ifsc_code ||
      bankDetails.ifscCode ||
      bankDetails.ifsc_code ||
      "",
    accountName:
      metadata.accountName ||
      metadata.account_name ||
      bankDetails.accountName ||
      bankDetails.account_holder_name ||
      "",
    bankName:
      metadata.bankName ||
      metadata.bank_name ||
      bankDetails.bankName ||
      bankDetails.bank_name ||
      "",
  };
}

function isImagePath(value) {
  if (!value || typeof value !== "string") return false;
  return (
    /^(https?:\/\/|\/|data:image)/i.test(value) ||
    /\.(png|jpe?g|gif|svg|webp)(\?|$)/i.test(value)
  );
}

function resolveImagePath(method) {
  if (isImagePath(method.imagePath)) return method.imagePath;
  if (isImagePath(method.icon)) return method.icon;
  return "";
}

function getStepKey(method) {
  const name = String(method.name || "").toLowerCase();
  const type = String(method.type || "").toLowerCase();
  const metadata = method.metadata || {};

  // Name / type cues for the two manual screens we support.
  if (
    name.includes("upi") ||
    name === "zipzopay" ||
    type === "upi" ||
    type === "manual_upi"
  ) {
    return "UPI";
  }
  if (name.includes("imps") || type === "imps") {
    return "IMPS";
  }

  // Generic type:"manual" (e.g. name "jhg") has no UPI/IMPS in the name.
  // Deposit.js only renders UPI | IMPS — any other stepKey shows a blank body.
  // Default these to the same manual flow as "Manual IMPS/UPI".
  if (type === "manual" || type === "bank_transfer") {
    if (metadata.accountNo || metadata.account_no) return "IMPS";
    return "UPI";
  }

  if (metadata.upiId || metadata.upi_id) return "UPI";
  if (metadata.accountNo || metadata.account_no) return "IMPS";

  return String(method.name || "").toUpperCase();
}

function getProviderInitial(method) {
  const label = method.name || method.provider || "?";
  return String(label).charAt(0).toUpperCase();
}

function getDisplayTitle(method) {
  return method.name || method.provider || "";
}

function getDisplayMode(method) {
  const type = String(method.type || "").toLowerCase();

  if (type === "manual") {
    return { label: "MANUAL", kind: "manual" };
  }
  if (type === "manual_upi") {
    return { label: "MANUAL UPI", kind: "manual_upi" };
  }
  if (type === "gateway") {
    return { label: "GATEWAY", kind: "gateway" };
  }

  return { label: "NEW METHOD", kind: "new" };
}

function getDisplayTagline(method) {
  const stepKey = getStepKey(method);
  if (stepKey === "UPI") return "UNIFIED PAYMENTS INTERFACE";
  if (stepKey === "IMPS") return "IMMEDIATE PAYMENT SERVICE";
  return String(method.provider || method.name || "").toUpperCase();
}

/** Map player API payment method to the shape used by deposit UI. */
export function normalizePaymentMethod(method) {
  const metadata = method.metadata || {};

  const displayMode = getDisplayMode(method);

  return {
    ...method,
    imagePath: resolveImagePath(method),
    providerInitial: getProviderInitial(method),
    displayTitle: getDisplayTitle(method),
    displayMode: displayMode.label,
    displayModeKind: displayMode.kind,
    displayTagline: getDisplayTagline(method),
    stepKey: getStepKey(method),
    minAmonut: formatAmount(method.min_amount ?? method.minAmonut),
    maxAmonut: formatAmount(method.max_amount ?? method.maxAmonut),
    status: isActiveStatus(method.status),
    isManualPayment: isManualPaymentMethod(method),
    details: mapPaymentDetails(metadata),
  };
}

export function normalizePaymentMethods(methods = []) {
  if (!Array.isArray(methods)) return [];
  return methods.map(normalizePaymentMethod);
}
